/**
 * Headless 1auth client.
 *
 * Used by integrators that hold their own ECDSA signer in localStorage
 * and produce signatures for an ERC-7579 validator other than the
 * passkey one (typically `@rhinestone/sdk` experimental SmartSession).
 *
 * Responsibilities:
 *  - Fetch sponsorship JWTs from the app's own backend.
 *  - Call `/api/intent/prepare?signingMode=headless` to get an
 *    orchestrator-quoted `intentOp` + `digestResult`.
 *  - Forward pre-encoded validator-prefixed signatures to the new
 *    `/api/intent/headless-execute` route.
 *  - Drive the existing passkey dialog (via {@link OneAuthClient}) for
 *    the one-time SmartSession install ceremony.
 *
 * 1auth itself stays ignorant of session keys — the on-chain
 * SmartSession validator is the only authority over what a session key
 * may sign for. The host app persists `{ sessionKeyAddress, permissionId,
 * accountAddress, permissions }` in its own storage.
 */

import type {
  HeadlessIntentOptions,
  HeadlessIntentStatusResult,
  HeadlessPrepareResult,
  HeadlessSubmitOptions,
  HeadlessSubmitResult,
  InstallSmartSessionOptions,
  InstallSmartSessionResult,
  IntentTokenRequest,
  PasskeyProviderConfig,
  SponsorshipCallbackConfig,
  SponsorshipConfig,
} from "./types";
import { tokenFetchError } from "./sponsorship-fetch";

const DEFAULT_PROVIDER_URL = "https://passkey.1auth.app";

const MISSING_APP_CREDENTIALS_MESSAGE =
  "No sponsorship configured on OneAuthHeadlessClient. Every user intent must carry the app's JWT — pass `sponsorship: { accessToken, getExtensionToken }` (or URL form) to the constructor.";

type AccessTokenFetchResult =
  | { ok: true; accessToken: string }
  | { ok: false; code?: "MISSING_APP_CREDENTIALS"; message: string };

type ExtensionTokenFetchResult =
  | { ok: true; extensionToken: string | undefined }
  | { ok: false; message: string };

/**
 * Mirrors `normalizeSponsorship` in `client.ts`. Kept local so the
 * headless entry point doesn't pull in the whole OneAuthClient module
 * graph for callers that only need the headless surface.
 */
function normalizeSponsorship(
  config: SponsorshipConfig | undefined,
): SponsorshipCallbackConfig | null {
  if (!config) return null;
  if ("accessToken" in config && "getExtensionToken" in config) {
    return config;
  }
  const { accessTokenUrl, extensionTokenUrl } = config;
  return {
    accessToken: async () => {
      const response = await fetch(accessTokenUrl, { credentials: "include" });
      if (!response.ok) {
        throw await tokenFetchError("access token", response);
      }
      const data = (await response.json()) as { token?: string };
      if (!data.token) {
        throw new Error("Access token response missing `token` field");
      }
      return data.token;
    },
    getExtensionToken: async (intentOp: string) => {
      const response = await fetch(extensionTokenUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intentOp }),
      });
      if (!response.ok) {
        throw await tokenFetchError("extension token", response);
      }
      const data = (await response.json()) as { token?: string };
      if (!data.token) {
        throw new Error("Extension token response missing `token` field");
      }
      return data.token;
    },
  };
}

export class OneAuthHeadlessClient {
  private providerUrl: string;
  private clientId?: string;
  private sponsorship: SponsorshipCallbackConfig | null;

  constructor(config: PasskeyProviderConfig) {
    this.providerUrl = config.providerUrl || DEFAULT_PROVIDER_URL;
    this.clientId = config.clientId;
    this.sponsorship = normalizeSponsorship(config.sponsorship);
  }

  setSponsorship(sponsorship: SponsorshipConfig | undefined): void {
    this.sponsorship = normalizeSponsorship(sponsorship);
  }

  // ---------------------------------------------------------------------------
  // Sponsorship JWT fetchers
  // ---------------------------------------------------------------------------

  private async fetchAccessToken(): Promise<AccessTokenFetchResult> {
    if (!this.sponsorship) {
      return {
        ok: false,
        code: "MISSING_APP_CREDENTIALS",
        message: MISSING_APP_CREDENTIALS_MESSAGE,
      };
    }
    try {
      const accessToken = await this.sponsorship.accessToken();
      return { ok: true, accessToken };
    } catch (err) {
      return {
        ok: false,
        message: err instanceof Error ? err.message : String(err),
      };
    }
  }

  /** Mint one digest-bound extension grant from a canonical intent input. */
  private async fetchExtensionToken(intentInput: string): Promise<ExtensionTokenFetchResult> {
    if (!this.sponsorship) {
      return { ok: false, message: MISSING_APP_CREDENTIALS_MESSAGE };
    }
    try {
      const token = await this.sponsorship.getExtensionToken(intentInput);
      return { ok: true, extensionToken: token };
    } catch (err) {
      return {
        ok: false,
        message: err instanceof Error ? err.message : String(err),
      };
    }
  }

  // ---------------------------------------------------------------------------
  // Intent prepare / submit
  // ---------------------------------------------------------------------------

  /**
   * Quote an intent without opening any dialog. Returns the orchestrator's
   * `intentOp` plus the digest the caller must sign with their own
   * validator (e.g. SmartSession). The caller is responsible for
   * encoding signatures and submitting via {@link submitIntent}.
   */
  async prepareIntent(options: HeadlessIntentOptions): Promise<HeadlessPrepareResult> {
    if (!options.accountAddress) {
      throw new Error("prepareIntent requires `accountAddress`");
    }

    const accessTokenResult = await this.fetchAccessToken();
    if (!accessTokenResult.ok) {
      throw new Error(`Sponsorship access token fetch failed: ${accessTokenResult.message}`);
    }

    // bigint → string conversion for token requests; matches what the
    // dialog flow does before serializing requestBody.
    const tokenRequests = options.tokenRequests?.map((tr: IntentTokenRequest) => ({
      token: tr.token,
      amount: tr.amount.toString(),
    }));

    const body = {
      accountAddress: options.accountAddress,
      targetChain: options.targetChain,
      calls: options.calls,
      tokenRequests,
      sourceAssets: options.sourceAssets,
      sourceChainId: options.sourceChainId,
      sessionKeyHandle: options.sessionKeyHandle,
      clientId: this.clientId,
      signingMode: "headless" as const,
      auth: { accessToken: accessTokenResult.accessToken },
    };
    const sponsorshipMode = options.sponsorshipMode ?? "required";

    const requestPrepare = async (requestBody: Record<string, unknown>) => {
      const response = await fetch(`${this.providerUrl}/api/intent/prepare`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(errorData.error || `Prepare failed (${response.status})`);
      }
      return response;
    };

    const selfFunded = async (): Promise<HeadlessPrepareResult> => ({
      ...((await requestPrepare({
        ...body,
        quoteMode: "final",
        sponsorshipMode: "self-funded",
      })).json() as Promise<HeadlessPrepareResult>),
      sponsorshipMode: "self-funded",
    });

    if (sponsorshipMode === "disabled") return selfFunded();

    const draftResponse = await requestPrepare({
      ...body,
      quoteMode: "draft",
      sponsorshipMode: "required",
    });
    const draft = (await draftResponse.json()) as { sponsorshipIntentInput?: unknown };
    if (draft.sponsorshipIntentInput === undefined) {
      throw new Error("Prepare response missing sponsorship intent input");
    }
    const extensionResult = await this.fetchExtensionToken(
      JSON.stringify(draft.sponsorshipIntentInput),
    );
    if (!extensionResult.ok || !extensionResult.extensionToken) {
      if (sponsorshipMode === "preferred") return selfFunded();
      throw new Error(extensionResult.ok ? "Extension token missing" : extensionResult.message);
    }
    let finalResponse: Response;
    try {
      finalResponse = await requestPrepare({
        ...body,
        quoteMode: "final",
        sponsorshipMode: "required",
        auth: {
          accessToken: accessTokenResult.accessToken,
          extensionToken: extensionResult.extensionToken,
        },
      });
    } catch (error) {
      // Preferred sponsorship includes quote-time rejection, not only token
      // minting failure. Re-quote explicitly without sponsorship.
      if (sponsorshipMode === "preferred") return selfFunded();
      throw error;
    }
    return {
      ...((await finalResponse.json()) as HeadlessPrepareResult),
      sponsorshipMode: "sponsored",
    };
  }

  /**
   * Forward a pre-signed intent using a fresh single-use execution grant.
   * The quote grant was consumed during prepare and must never be replayed.
   */
  async submitIntent(options: HeadlessSubmitOptions): Promise<HeadlessSubmitResult> {
    const accessTokenResult = await this.fetchAccessToken();
    if (!accessTokenResult.ok) {
      throw new Error(`Sponsorship access token fetch failed: ${accessTokenResult.message}`);
    }
    let executionToken: string | undefined;
    if (options.sponsorshipMode === "sponsored") {
      const extensionResult = await this.fetchExtensionToken(options.intentOp);
      if (!extensionResult.ok || !extensionResult.extensionToken) {
        throw new Error(extensionResult.ok ? "Execution extension token missing" : extensionResult.message);
      }
      executionToken = extensionResult.extensionToken;
    }

    const body = {
      intentOp: options.intentOp,
      accountAddress: options.accountAddress,
      targetChain: options.targetChain,
      calls: options.calls,
      expiresAt: options.expiresAt,
      originSignatures: options.originSignatures,
      destinationSignature: options.destinationSignature,
      targetExecutionSignature: options.targetExecutionSignature,
      auth: {
        accessToken: accessTokenResult.accessToken,
        ...(executionToken && { extensionToken: executionToken }),
      },
    };

    const response = await fetch(`${this.providerUrl}/api/intent/headless-execute`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(errorData.error || `Headless execute failed (${response.status})`);
    }

    return (await response.json()) as HeadlessSubmitResult;
  }

  // ---------------------------------------------------------------------------
  // Status / wait — proxies to the existing routes (no headless variant needed).
  // ---------------------------------------------------------------------------

  /**
   * Fetch the latest known status for a submitted headless intent.
   * This is a non-blocking read and may return `pending` before a
   * solver has produced a fill transaction hash.
   */
  async getIntentStatus(intentId: string): Promise<HeadlessIntentStatusResult> {
    const response = await fetch(`${this.providerUrl}/api/intent/status/${intentId}`);
    if (!response.ok) {
      throw new Error(`Status fetch failed (${response.status})`);
    }
    return (await response.json()) as HeadlessIntentStatusResult;
  }

  /**
   * Wait for a submitted headless intent to produce a status update and,
   * when available, the final fill transaction hash. The wait endpoint
   * needs the opaque `transactionResult` returned by submit because 1auth
   * does not persist that SDK object in its database.
   */
  async waitForIntent(
    intentId: string,
    transactionResult: unknown,
  ): Promise<HeadlessIntentStatusResult> {
    if (!transactionResult) {
      throw new Error("waitForIntent requires submitResult.transactionResult");
    }

    const response = await fetch(`${this.providerUrl}/api/intent/wait/${intentId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transactionResult }),
    });
    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as {
        error?: string;
        details?: string;
      };
      throw new Error(
        errorData.details || errorData.error || `Wait fetch failed (${response.status})`,
      );
    }
    return (await response.json()) as HeadlessIntentStatusResult;
  }

  // ---------------------------------------------------------------------------
  // SmartSession install (one-time, passkey-signed)
  // ---------------------------------------------------------------------------

  /**
   * Resolve the SmartSession install call(s) for `sessionKeyAddress`
   * with the supplied `permissions`.
   *
   * Returns `{ install, sessionKeyHandle }`:
   *
   *  - `install` is a `{ targetChain, calls }` pair the caller feeds
   *    straight into `OneAuthClient.sendIntent({ targetChain, calls })`
   *    so the user passkey-signs the install via the existing dialog.
   *  - `sessionKeyHandle` contains the deterministic `permissionId`
   *    plus metadata to persist in localStorage. Persist this BEFORE
   *    submitting the install so a refresh during signing doesn't
   *    lose the handle.
   *
   * If the validator is already installed on the user's account,
   * `install.calls` is empty and `install.alreadyInstalled` is true.
   * The caller should still persist `sessionKeyHandle` — the on-chain
   * session enabling happens at first headless use via SmartSession's
   * ENABLE-mode signature wrapping (see follow-up).
   */
  async installSmartSession(
    options: InstallSmartSessionOptions,
  ): Promise<InstallSmartSessionResult> {
    if (!options.accountAddress) {
      throw new Error(
        "installSmartSession requires `accountAddress`",
      );
    }
    if (options.enableSessionSignature && !("credentialId" in options) ) {
      throw new Error(
        "installSmartSession requires `credentialId` with `enableSessionSignature`",
      );
    }

    const accessTokenResult = await this.fetchAccessToken();
    if (!accessTokenResult.ok) {
      throw new Error(
        `Sponsorship access token fetch failed: ${accessTokenResult.message}`,
      );
    }

    const body = {
      accountAddress: options.accountAddress,
      targetChain: options.targetChain,
      sessionKeyAddress: options.sessionKeyAddress,
      permissions: options.permissions,
      validUntil: options.validUntil,
      validAfter: options.validAfter,
      maxUses: options.maxUses,
      enableSessionSignature: options.enableSessionSignature,
      credentialId: "credentialId" in options ? options.credentialId : undefined,
      label: options.label,
      auth: { accessToken: accessTokenResult.accessToken },
    };

    const response = await fetch(`${this.providerUrl}/api/sessions/install`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      throw new Error(errorData.error || `Install failed (${response.status})`);
    }

    return (await response.json()) as InstallSmartSessionResult;
  }
}
